import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

type Section = 'privacy' | 'terms' | 'cookies' | 'gdpr' | 'accessibility';

const Legal: React.FC = () => {
  const [activeSection, setActiveSection] = useState<Section>('privacy');
  const location = useLocation();

  useEffect(() => {
    // Check if there's a hash in the URL and set the active section
    const hash = location.hash.replace('#', '') as Section;
    if (hash && ['privacy', 'terms', 'cookies', 'gdpr', 'accessibility'].includes(hash)) {
      setActiveSection(hash);
    }
  }, [location]);

  const sections = [
    { id: 'privacy' as Section, title: 'Privacy Policy', icon: '🔒' },
    { id: 'terms' as Section, title: 'Terms of Service', icon: '📜' },
    { id: 'cookies' as Section, title: 'Cookie Policy', icon: '🍪' },
    { id: 'gdpr' as Section, title: 'GDPR Compliance', icon: '🇪🇺' },
    { id: 'accessibility' as Section, title: 'Accessibility', icon: '♿' }
  ];

  return (
    <section className="py-20 relative overflow-hidden min-h-screen">
      {/* Background gradient */}
      <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(circle at 50% 50%, rgba(99, 102, 241, 0.2), transparent 70%)', filter: 'blur(60px)' }}></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span
              style={{
                background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              Legal Information
            </span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Your privacy and rights matter to us. Please review our policies below.
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                activeSection === section.id
                  ? 'text-white shadow-lg'
                  : 'text-gray-400 hover:text-white'
              }`}
              style={{
                backgroundColor: activeSection === section.id ? 'var(--color-primary)' : 'rgba(255, 255, 255, 0.05)',
                border: activeSection === section.id ? 'none' : '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <span className="mr-2">{section.icon}</span>
              {section.title}
            </button>
          ))}
        </div>

        {/* Content */}
        <motion.div
          key={activeSection}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="glass p-8 md:p-12 rounded-2xl"
          style={{ background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
        >
          {activeSection === 'privacy' && <PrivacyPolicy />}
          {activeSection === 'terms' && <TermsOfService />}
          {activeSection === 'cookies' && <CookiePolicy />}
          {activeSection === 'gdpr' && <GDPRCompliance />}
          {activeSection === 'accessibility' && <AccessibilityStatement />}
        </motion.div>

        {/* Last Updated */}
        <p className="text-center text-gray-500 text-sm mt-8">
          Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
      </div>
    </section>
  );
};

const PrivacyPolicy: React.FC = () => (
  <div className="prose prose-invert max-w-none">
    <h2 className="text-3xl font-bold text-white mb-6">Privacy Policy</h2>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">1. Introduction</h3>
    <p className="text-gray-300 mb-4">
      At Kairos ("we," "us," or "our"), we are committed to protecting your privacy and ensuring the security of your personal information.
      This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">2. Information We Collect</h3>
    <p className="text-gray-300 mb-4">We collect information that you provide directly to us, including:</p>
    <ul className="text-gray-300 mb-4 list-disc pl-6">
      <li>Personal identification information (name, email address, phone number)</li>
      <li>Company information (company name, industry, size)</li>
      <li>Communication preferences</li>
      <li>Usage data and analytics</li>
      <li>Technical information (IP address, browser type, device information)</li>
    </ul>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">3. How We Use Your Information</h3>
    <p className="text-gray-300 mb-4">We use the information we collect to:</p>
    <ul className="text-gray-300 mb-4 list-disc pl-6">
      <li>Provide, maintain, and improve our services</li>
      <li>Process your requests and transactions</li>
      <li>Send you technical notices, updates, and support messages</li>
      <li>Respond to your comments and questions</li>
      <li>Analyze usage patterns and optimize user experience</li>
      <li>Detect, prevent, and address technical issues and fraudulent activity</li>
    </ul>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">4. Information Sharing and Disclosure</h3>
    <p className="text-gray-300 mb-4">
      We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
    </p>
    <ul className="text-gray-300 mb-4 list-disc pl-6">
      <li>With your consent</li>
      <li>To comply with legal obligations</li>
      <li>To protect our rights and prevent fraud</li>
      <li>With service providers who assist in our operations (under strict confidentiality agreements)</li>
      <li>In connection with a business transfer or acquisition</li>
    </ul>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">5. Data Security</h3>
    <p className="text-gray-300 mb-4">
      We implement industry-standard security measures to protect your personal information from unauthorized access, disclosure, alteration,
      and destruction. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">6. Your Rights and Choices</h3>
    <p className="text-gray-300 mb-4">You have the right to:</p>
    <ul className="text-gray-300 mb-4 list-disc pl-6">
      <li>Access and receive a copy of your personal data</li>
      <li>Rectify inaccurate or incomplete data</li>
      <li>Request deletion of your data</li>
      <li>Object to or restrict processing of your data</li>
      <li>Withdraw consent at any time</li>
      <li>Data portability</li>
    </ul>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">7. Contact Us</h3>
    <p className="text-gray-300 mb-4">
      If you have questions about this Privacy Policy, please contact us at <a href="mailto:kairos@m-kaos.com" className="text-blue-400 hover:underline">kairos@m-kaos.com</a>
    </p>
  </div>
);

const TermsOfService: React.FC = () => (
  <div className="prose prose-invert max-w-none">
    <h2 className="text-3xl font-bold text-white mb-6">Terms of Service</h2>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">1. Acceptance of Terms</h3>
    <p className="text-gray-300 mb-4">
      By accessing and using Kairos services, you accept and agree to be bound by the terms and provisions of this agreement.
      If you do not agree to these terms, please do not use our services.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">2. Description of Service</h3>
    <p className="text-gray-300 mb-4">
      Kairos provides automation solutions and business process optimization services. We reserve the right to modify, suspend,
      or discontinue any aspect of our services at any time without prior notice.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">3. User Obligations</h3>
    <p className="text-gray-300 mb-4">You agree to:</p>
    <ul className="text-gray-300 mb-4 list-disc pl-6">
      <li>Provide accurate and complete information</li>
      <li>Maintain the security of your account credentials</li>
      <li>Use our services only for lawful purposes</li>
      <li>Not attempt to interfere with or disrupt our services</li>
      <li>Not engage in any fraudulent, abusive, or harmful activities</li>
      <li>Comply with all applicable laws and regulations</li>
    </ul>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">4. Intellectual Property</h3>
    <p className="text-gray-300 mb-4">
      All content, features, and functionality of our services, including but not limited to text, graphics, logos, software,
      and designs, are owned by Kairos and are protected by international copyright, trademark, and other intellectual property laws.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">5. Limitation of Liability</h3>
    <p className="text-gray-300 mb-4">
      To the fullest extent permitted by law, Kairos shall not be liable for any indirect, incidental, special, consequential,
      or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data,
      use, goodwill, or other intangible losses resulting from your use of our services.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">6. Indemnification</h3>
    <p className="text-gray-300 mb-4">
      You agree to indemnify, defend, and hold harmless Kairos and its officers, directors, employees, and agents from any claims,
      liabilities, damages, losses, and expenses arising out of or in any way connected with your access to or use of our services.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">7. Termination</h3>
    <p className="text-gray-300 mb-4">
      We reserve the right to terminate or suspend your access to our services immediately, without prior notice or liability,
      for any reason whatsoever, including violation of these Terms of Service.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">8. Governing Law</h3>
    <p className="text-gray-300 mb-4">
      These Terms shall be governed by and construed in accordance with applicable laws, without regard to its conflict of law provisions.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">9. Changes to Terms</h3>
    <p className="text-gray-300 mb-4">
      We reserve the right to modify or replace these Terms at any time. We will provide notice of any material changes
      by posting the new Terms on this page with an updated effective date.
    </p>
  </div>
);

const CookiePolicy: React.FC = () => (
  <div className="prose prose-invert max-w-none">
    <h2 className="text-3xl font-bold text-white mb-6">Cookie Policy</h2>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">1. What Are Cookies</h3>
    <p className="text-gray-300 mb-4">
      Cookies are small text files that are placed on your device when you visit our website. They help us provide you with
      a better experience by remembering your preferences and understanding how you use our site.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">2. Types of Cookies We Use</h3>

    <h4 className="text-lg font-semibold text-white mt-6 mb-3">Essential Cookies</h4>
    <p className="text-gray-300 mb-4">
      These cookies are necessary for the website to function properly. They enable core functionality such as security,
      network management, and accessibility.
    </p>

    <h4 className="text-lg font-semibold text-white mt-6 mb-3">Analytics Cookies</h4>
    <p className="text-gray-300 mb-4">
      We use analytics cookies to understand how visitors interact with our website. This helps us improve our services
      and user experience. These cookies collect information anonymously.
    </p>

    <h4 className="text-lg font-semibold text-white mt-6 mb-3">Functionality Cookies</h4>
    <p className="text-gray-300 mb-4">
      These cookies allow us to remember choices you make (such as your language preference) and provide enhanced,
      personalized features.
    </p>

    <h4 className="text-lg font-semibold text-white mt-6 mb-3">Marketing Cookies</h4>
    <p className="text-gray-300 mb-4">
      These cookies are used to deliver advertisements more relevant to you and your interests. They may also be used
      to limit the number of times you see an advertisement and measure the effectiveness of advertising campaigns.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">3. Third-Party Cookies</h3>
    <p className="text-gray-300 mb-4">
      We may use third-party services such as Google Analytics, which may set cookies to help us analyze website usage.
      These third parties have their own privacy policies governing their use of information.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">4. Managing Cookies</h3>
    <p className="text-gray-300 mb-4">
      Most web browsers allow you to control cookies through their settings. You can choose to block or delete cookies,
      but this may impact your experience on our website. To learn more about cookie management:
    </p>
    <ul className="text-gray-300 mb-4 list-disc pl-6">
      <li>Chrome: Settings &gt; Privacy and security &gt; Cookies</li>
      <li>Firefox: Preferences &gt; Privacy & Security &gt; Cookies</li>
      <li>Safari: Preferences &gt; Privacy &gt; Cookies</li>
      <li>Edge: Settings &gt; Privacy, search, and services &gt; Cookies</li>
    </ul>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">5. Updates to This Policy</h3>
    <p className="text-gray-300 mb-4">
      We may update this Cookie Policy from time to time to reflect changes in technology or legal requirements.
      Please check this page periodically for updates.
    </p>
  </div>
);

const GDPRCompliance: React.FC = () => (
  <div className="prose prose-invert max-w-none">
    <h2 className="text-3xl font-bold text-white mb-6">GDPR Compliance</h2>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">1. Our Commitment to GDPR</h3>
    <p className="text-gray-300 mb-4">
      Kairos is committed to complying with the General Data Protection Regulation (GDPR) and protecting the rights of individuals
      in the European Union (EU) and European Economic Area (EEA). We implement appropriate technical and organizational measures
      to ensure data protection by design and by default.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">2. Legal Basis for Processing</h3>
    <p className="text-gray-300 mb-4">We process your personal data based on the following legal grounds:</p>
    <ul className="text-gray-300 mb-4 list-disc pl-6">
      <li><strong>Consent:</strong> You have given clear consent for us to process your personal data for specific purposes</li>
      <li><strong>Contract:</strong> Processing is necessary for a contract we have with you</li>
      <li><strong>Legal obligation:</strong> Processing is necessary for us to comply with the law</li>
      <li><strong>Legitimate interests:</strong> Processing is necessary for our legitimate interests or the legitimate interests of a third party</li>
    </ul>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">3. Your Rights Under GDPR</h3>
    <p className="text-gray-300 mb-4">Under GDPR, you have the following rights:</p>

    <h4 className="text-lg font-semibold text-white mt-6 mb-3">Right to Access</h4>
    <p className="text-gray-300 mb-4">
      You have the right to request copies of your personal data. We may charge a small fee for this service.
    </p>

    <h4 className="text-lg font-semibold text-white mt-6 mb-3">Right to Rectification</h4>
    <p className="text-gray-300 mb-4">
      You have the right to request correction of any information you believe is inaccurate or incomplete.
    </p>

    <h4 className="text-lg font-semibold text-white mt-6 mb-3">Right to Erasure</h4>
    <p className="text-gray-300 mb-4">
      You have the right to request deletion of your personal data under certain conditions.
    </p>

    <h4 className="text-lg font-semibold text-white mt-6 mb-3">Right to Restrict Processing</h4>
    <p className="text-gray-300 mb-4">
      You have the right to request restriction of processing of your personal data under certain conditions.
    </p>

    <h4 className="text-lg font-semibold text-white mt-6 mb-3">Right to Object</h4>
    <p className="text-gray-300 mb-4">
      You have the right to object to our processing of your personal data under certain conditions.
    </p>

    <h4 className="text-lg font-semibold text-white mt-6 mb-3">Right to Data Portability</h4>
    <p className="text-gray-300 mb-4">
      You have the right to request transfer of your data to another organization or directly to you under certain conditions.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">4. Data Protection Officer</h3>
    <p className="text-gray-300 mb-4">
      We have appointed a Data Protection Officer (DPO) who is responsible for overseeing our data protection strategy and
      ensuring compliance with GDPR requirements. You can contact our DPO at <a href="mailto:kairos@m-kaos.com" className="text-blue-400 hover:underline">kairos@m-kaos.com</a>
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">5. Data Retention</h3>
    <p className="text-gray-300 mb-4">
      We retain personal data only for as long as necessary to fulfill the purposes for which it was collected, including
      legal, accounting, or reporting requirements. When data is no longer needed, we securely delete or anonymize it.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">6. International Data Transfers</h3>
    <p className="text-gray-300 mb-4">
      If we transfer personal data outside the EU/EEA, we ensure appropriate safeguards are in place, such as Standard
      Contractual Clauses approved by the European Commission or transfers to countries with adequacy decisions.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">7. Data Breach Notification</h3>
    <p className="text-gray-300 mb-4">
      In the event of a data breach that poses a risk to your rights and freedoms, we will notify the relevant supervisory
      authority within 72 hours and inform affected individuals without undue delay.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">8. Exercising Your Rights</h3>
    <p className="text-gray-300 mb-4">
      To exercise any of your GDPR rights, please contact us at <a href="mailto:kairos@m-kaos.com" className="text-blue-400 hover:underline">kairos@m-kaos.com</a>.
      We will respond to your request within one month. You also have the right to lodge a complaint with your local supervisory authority.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">9. Children's Privacy</h3>
    <p className="text-gray-300 mb-4">
      Our services are not intended for children under 16 years of age. We do not knowingly collect personal data from children.
      If we learn we have collected data from a child, we will delete it immediately.
    </p>
  </div>
);

const AccessibilityStatement: React.FC = () => (
  <div className="prose prose-invert max-w-none">
    <h2 className="text-3xl font-bold text-white mb-6">Accessibility Statement</h2>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">1. Our Commitment to Accessibility</h3>
    <p className="text-gray-300 mb-4">
      Kairos is committed to ensuring digital accessibility for people with disabilities. We are continually improving
      the user experience for everyone and applying the relevant accessibility standards to ensure we provide equal
      access to all of our users.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">2. Conformance Standards</h3>
    <p className="text-gray-300 mb-4">
      We strive to conform to Level AA of the World Wide Web Consortium (W3C) Web Content Accessibility Guidelines 2.1 (WCAG 2.1).
      These guidelines explain how to make web content more accessible for people with disabilities and user-friendly for everyone.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">3. Accessibility Features</h3>
    <p className="text-gray-300 mb-4">Our website includes the following accessibility features:</p>
    <ul className="text-gray-300 mb-4 list-disc pl-6">
      <li>Semantic HTML markup for proper content structure</li>
      <li>Keyboard navigation support for all interactive elements</li>
      <li>ARIA labels and landmarks for screen reader compatibility</li>
      <li>Sufficient color contrast ratios for text and UI components</li>
      <li>Responsive design that adapts to different screen sizes and zoom levels</li>
      <li>Alternative text for images and non-text content</li>
      <li>Clear and consistent navigation structure</li>
      <li>Focus indicators for keyboard users</li>
    </ul>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">4. Assistive Technologies</h3>
    <p className="text-gray-300 mb-4">
      Our website is designed to be compatible with the following assistive technologies:
    </p>
    <ul className="text-gray-300 mb-4 list-disc pl-6">
      <li>Screen readers (JAWS, NVDA, VoiceOver)</li>
      <li>Screen magnification software</li>
      <li>Speech recognition software</li>
      <li>Alternative input devices</li>
    </ul>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">5. Known Limitations</h3>
    <p className="text-gray-300 mb-4">
      Despite our best efforts to ensure accessibility, there may be some limitations. We are actively working to
      remediate any issues and improve the accessibility of our website. Currently known limitations include:
    </p>
    <ul className="text-gray-300 mb-4 list-disc pl-6">
      <li>Some third-party embedded content may not be fully accessible</li>
      <li>Older PDF documents may not meet current accessibility standards</li>
      <li>Some complex interactive features may require additional accessibility improvements</li>
    </ul>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">6. Feedback and Contact</h3>
    <p className="text-gray-300 mb-4">
      We welcome your feedback on the accessibility of our website. If you encounter any accessibility barriers or have
      suggestions for improvement, please contact us:
    </p>
    <ul className="text-gray-300 mb-4 list-disc pl-6">
      <li>Email: <a href="mailto:kairos@m-kaos.com" className="text-blue-400 hover:underline">kairos@m-kaos.com</a></li>
      <li>We aim to respond to accessibility feedback within 5 business days</li>
    </ul>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">7. Accessibility Testing</h3>
    <p className="text-gray-300 mb-4">
      Our website undergoes regular accessibility testing using:
    </p>
    <ul className="text-gray-300 mb-4 list-disc pl-6">
      <li>Automated accessibility testing tools</li>
      <li>Manual testing with assistive technologies</li>
      <li>User testing with people with disabilities</li>
      <li>Third-party accessibility audits</li>
    </ul>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">8. Continuous Improvement</h3>
    <p className="text-gray-300 mb-4">
      Accessibility is an ongoing effort. We regularly review our website and update our practices to ensure we maintain
      and improve accessibility. We also provide training to our team members on accessibility best practices.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">9. Third-Party Content</h3>
    <p className="text-gray-300 mb-4">
      While we strive to ensure that all content on our website is accessible, some third-party content may not be under
      our direct control. We work with our third-party vendors to ensure their content meets accessibility standards.
    </p>

    <h3 className="text-xl font-semibold text-white mt-8 mb-4">10. Last Review Date</h3>
    <p className="text-gray-300 mb-4">
      This accessibility statement was last reviewed on {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.
    </p>
  </div>
);

export default Legal;
